import React, { useState, useEffect } from 'react'
import { Outlet, Link, useLocation, NavLink, Navigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { LoadingScreen } from '@/components/ui/LoadingScreen'
import { AnalyticsTracker } from '@/components/ui/AnalyticsTracker'

const NAV_ITEMS = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    to: '/customers',
    label: 'Customers',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    to: '/projects',
    label: 'Projects',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
  {
    to: '/requests/new',
    label: 'AI Intake',
    icon: (
      <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    to: '/templates',
    label: 'Templates',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    to: '/billing',
    label: 'Billing',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
]

function NavItem({ to, label, icon }: { to: string; label: string; icon: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
          isActive
            ? 'bg-brand-50 text-brand-700'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
        ].join(' ')
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  )
}

interface Subscription {
  owner_id: string
  stripe_customer_id: string
  stripe_subscription_id: string
  stripe_price_id: string
  status: string
  current_period_end: string
  cancel_at_period_end: boolean
  updated_at: string
}

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const { user, profile, loading, signOut, refreshProfile } = useAuth()

  const [inviteToken, setInviteToken] = useState<string | null>(null)
  const [inviteDetails, setInviteDetails] = useState<{
    valid: boolean
    cohort_name?: string
    masked_email?: string
    expires_at?: string
    error?: string
  } | null>(null)
  const [inviteStatusMsg, setInviteStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    // 1. Process URL search parameter
    const searchParams = new URLSearchParams(window.location.search)
    const token = searchParams.get('invite_token')
    if (token) {
      // Save to sessionStorage
      sessionStorage.setItem('pending_invite_token', token)
      setInviteToken(token)
      // Clean URL immediately
      window.history.replaceState(null, '', window.location.pathname)
    } else {
      const stored = sessionStorage.getItem('pending_invite_token')
      if (stored) {
        setInviteToken(stored)
      }
    }
  }, [location.pathname])

  // 2. Fetch invitation details if token exists
  useEffect(() => {
    if (!inviteToken || inviteDetails) return

    let active = true
    const fetchDetails = async () => {
      try {
        const { data, error } = await supabase.rpc('get_pilot_invitation_details', { p_token: inviteToken })
        if (error) throw error
        if (active) {
          const res = data as any
          if (res && res.valid) {
            setInviteDetails(res)
          } else {
            // Invalid, expired or not found - clean up
            sessionStorage.removeItem('pending_invite_token')
            setInviteToken(null)
            if (res && res.error) {
              setInviteStatusMsg({ type: 'error', text: `Invitation invalid: ${res.error}` })
            }
          }
        }
      } catch (err) {
        console.error('Error fetching invitation details:', err)
        if (active) {
          sessionStorage.removeItem('pending_invite_token')
          setInviteToken(null)
        }
      }
    }

    void fetchDetails()
    return () => { active = false }
  }, [inviteToken, inviteDetails])

  const handleAcceptInvite = async () => {
    if (!inviteToken) return
    setInviteStatusMsg(null)
    try {
      const { data, error } = await supabase.rpc('accept_pilot_invitation', { p_token: inviteToken })
      if (error) throw error
      
      const res = data as any
      if (res && res.ok) {
        sessionStorage.removeItem('pending_invite_token')
        setInviteToken(null)
        setInviteDetails(null)
        setInviteStatusMsg({ type: 'success', text: `Success! You have accepted the invitation to the "${res.cohort_name}" pilot.` })
        if (refreshProfile) {
          await refreshProfile()
        }
      } else {
        throw new Error(res?.error || 'Failed to accept invitation.')
      }
    } catch (err: any) {
      console.error('Accept invite error:', err)
      sessionStorage.removeItem('pending_invite_token')
      setInviteToken(null)
      setInviteDetails(null)
      setInviteStatusMsg({ type: 'error', text: err.message || 'Failed to accept invitation.' })
    }
  }

  const handleDismissInvite = () => {
    sessionStorage.removeItem('pending_invite_token')
    setInviteToken(null)
    setInviteDetails(null)
    setInviteStatusMsg(null)
  }

  const showOperatorNav = profile?.role === 'operator' || profile?.role === 'admin'

  const { data: needsReviewCount = 0 } = useQuery<number>({
    queryKey: ['operator-needs-review-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('service_requests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'needs_review')
      if (error) throw error
      return count || 0
    },
    enabled: !!user && showOperatorNav,
    refetchInterval: 30000,
  })

  const { data: subscription } = useQuery<Subscription | null>({
    queryKey: ['subscription'],
    queryFn: async () => {
      const response = (await supabase
        .from('subscriptions')
        .select('*')
        .maybeSingle()) as { data: Subscription | null; error: unknown }
      if (response.error) throw new Error('Database error')
      return response.data
    },
    enabled: !!user,
  })

  const isPaymentFailed = subscription?.status === 'past_due' || subscription?.status === 'unpaid'

  if (loading) {
    return <LoadingScreen />
  }

  if (!user) {
    return <Navigate to="/sign-in" replace state={{ from: location }} />
  }

  const isOnboardingPath = location.pathname === '/onboarding'
  const onboardingCompleted = !!profile?.onboarding_completed_at

  if (!onboardingCompleted && !isOnboardingPath) {
    return <Navigate to="/onboarding" replace />
  }

  if (onboardingCompleted && isOnboardingPath) {
    return <Navigate to="/dashboard" replace />
  }

  const handleSignOut = () => {
    sessionStorage.removeItem('pending_invite_token')
    setInviteToken(null)
    setInviteDetails(null)
    setInviteStatusMsg(null)
    void (async () => {
      const { error } = await signOut()
      if (error) {
        console.error('Logout error:', error)
      }
    })()
  }

  const displayName = profile?.contact_name || user.email || 'User'
  const displayPlan = `${profile?.plan_code || 'FREE'} plan`
  const initial = displayName.charAt(0).toUpperCase()

  return (
    <div className="min-h-screen bg-slate-50">
      <AnalyticsTracker />
      {/* ── Desktop sidebar ─────────────────────────────────────────────────── */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 lg:flex lg:flex-col border-r border-slate-200 bg-white z-30">
        <div className="flex h-16 items-center px-5 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2 font-bold text-slate-900">
            <span className="w-7 h-7 rounded-md bg-brand-600 flex items-center justify-center text-white text-xs font-bold">CL</span>
            ChangeLink
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" aria-label="Sidebar navigation">
          {NAV_ITEMS.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
          {showOperatorNav && (
            <NavLink
              to="/operator/queue"
              className={({ isActive }) =>
                [
                  'flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                ].join(' ')
              }
            >
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                <span>Review Queue</span>
              </div>
              {needsReviewCount > 0 && (
                <span className="bg-amber-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {needsReviewCount}
                </span>
              )}
            </NavLink>
          )}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center justify-between gap-3 px-2 py-1">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 text-sm font-semibold shrink-0">
                {initial}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{displayPlan}</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors shrink-0"
              title="Sign out"
              aria-label="Sign out"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {/* ── Mobile sidebar overlay ──────────────────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          onClick={() => { setSidebarOpen(false) }}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 flex flex-col bg-white border-r border-slate-200 transition-transform duration-300 lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Mobile navigation"
      >
        <div className="flex h-16 items-center justify-between px-5 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2 font-bold text-slate-900" onClick={() => { setSidebarOpen(false) }}>
            <span className="w-7 h-7 rounded-md bg-brand-600 flex items-center justify-center text-white text-xs font-bold">CL</span>
            ChangeLink
          </Link>
          <button
            className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg"
            onClick={() => { setSidebarOpen(false) }}
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            <div key={item.to} onClick={() => { setSidebarOpen(false) }}>
              <NavItem {...item} />
            </div>
          ))}
          {showOperatorNav && (
            <div onClick={() => { setSidebarOpen(false) }}>
              <NavLink
                to="/operator/queue"
                className={({ isActive }) =>
                  [
                    'flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                  ].join(' ')
                }
              >
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <span>Review Queue</span>
                </div>
                {needsReviewCount > 0 && (
                  <span className="bg-amber-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {needsReviewCount}
                  </span>
                )}
              </NavLink>
            </div>
          )}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center justify-between gap-3 px-2 py-1">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 text-sm font-semibold shrink-0">
                {initial}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{displayPlan}</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors shrink-0"
              title="Sign out"
              aria-label="Sign out"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main content ────────────────────────────────────────────────────── */}
      <div className="lg:pl-64">
        {inviteStatusMsg && (
          <div className={`text-white text-sm font-semibold px-4 py-3 flex items-center justify-between shadow-md z-30 ${
            inviteStatusMsg.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'
          }`}>
            <div className="flex items-center gap-2">
              <span>{inviteStatusMsg.type === 'success' ? '✅' : '❌'}</span>
              <span>{inviteStatusMsg.text}</span>
            </div>
            <button onClick={() => setInviteStatusMsg(null)} className="text-white/80 hover:text-white font-bold text-xs px-2 py-1 rounded">
              ✕
            </button>
          </div>
        )}

        {inviteDetails && inviteDetails.valid && (
          <div className="bg-gradient-to-r from-brand-600 to-indigo-600 text-white px-4 py-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-lg z-30">
            <div className="flex items-center gap-3">
              <span className="text-2xl" role="img" aria-label="party popper">🎉</span>
              <div>
                <p className="font-semibold text-sm">You were invited to the "{inviteDetails.cohort_name}" pilot!</p>
                <p className="text-xs text-brand-100 mt-0.5">This invitation is bound to {inviteDetails.masked_email}. Accept to activate your pilot account.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleAcceptInvite}
                className="bg-white text-brand-700 hover:bg-brand-50 px-4 py-2 rounded-lg text-xs font-bold transition-all shadow-md active:scale-95"
              >
                Accept invitation
              </button>
              <button
                onClick={handleDismissInvite}
                className="text-white/80 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {isPaymentFailed && (
          <div className="bg-rose-600 text-white text-sm font-semibold px-4 py-3 flex items-center justify-between shadow-md sticky top-0 z-30 animate-in slide-in-from-top duration-300">
            <div className="flex items-center gap-2">
              <span aria-hidden="true">⚠️</span>
              <span>Your last billing payment failed. Please update your card details to prevent interruption.</span>
            </div>
            <Link
              to="/billing"
              className="bg-white text-rose-700 hover:bg-rose-50 px-3 py-1 rounded-lg text-xs font-semibold transition-colors shrink-0"
            >
              Update Payment
            </Link>
          </div>
        )}
        {/* Top bar */}
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-slate-200 flex items-center gap-4 px-4 sm:px-6">
          <button
            className="lg:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
            onClick={() => { setSidebarOpen(true) }}
            aria-label="Open menu"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 className="flex-1 text-base font-semibold text-slate-900 truncate">
            {NAV_ITEMS.find((n) => location.pathname.startsWith(n.to))?.label ?? 'ChangeLink'}
          </h1>
          <Link
            to="/change-orders/new"
            className="inline-flex items-center gap-1.5 bg-brand-600 text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-brand-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">New change order</span>
            <span className="sm:hidden">New</span>
          </Link>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
          <Outlet />
        </main>
      </div>

      {/* ── Mobile bottom nav ───────────────────────────────────────────────── */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 flex"
        aria-label="Mobile bottom navigation"
      >
        {NAV_ITEMS.slice(0, 5).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              [
                'flex-1 flex flex-col items-center gap-1 py-2 text-xs font-medium transition-colors',
                isActive ? 'text-brand-600' : 'text-slate-500',
              ].join(' ')
            }
          >
            {item.icon}
            <span className="leading-none">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
